import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingColumns } from "@fortawesome/free-solid-svg-icons";
import { useTable } from "../../../hooks/useTable";
import { useSubTableColumns } from "../../../hooks/useSubTableColumns";
import { useSortTimeSheets } from "../../../../TimeSheetTable/hooks/useSortTimeSheets";
import { useCompanyCurrency } from "../../../../../../../utils/hooks/company";
import { getTimeSheetTotal } from "../../../../TimeSheetTable/useColumns/useTotalColumn";
import { getTimeSheetDuration } from "../../../../TimeSheetTable/useColumns/useDurationColumn";
import { renderDurationAsFormat } from "../../../../../../../utils/helpers/date";
import { currencyFormatter } from "../../../../../../../utils/helpers/currencyFormatter";
import Item from "./Item";

function Content({ userId, data: baseData, loading, groupBy, defaultUser, columnVisibility }) {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();
    const columns = useSubTableColumns({ groupBy });
    const userData = useMemo(() => {
        return baseData.filter((item) => item.user.id === userId);
    }, [baseData, userId]);
    const data = useSortTimeSheets(userData);

    const table = useTable({
        data,
        columns,
        totalItems: userData?.length || 0,
        loading,
        initialState: { columnVisibility }
    });
    const user = useMemo(() => {
        return userData?.[0]?.user ||
            defaultUser?.otherProfiles?.find((profile) => profile.id === userId) ||
            defaultUser;
    }, [userData, defaultUser, userId]);

    useEffect(() => {
        table.setColumnVisibility(columnVisibility);
    }, [columnVisibility, table]);

    const { totalAmount, workedHours, overtimeHours } = useMemo(() => {
        const totalAmount = userData?.reduce((total, item) => {
            return total + getTimeSheetTotal(item);
        }, 0);

        const workedHours = userData
            ?.filter((item) => {
                if (item.hourType === "unpaid") return false;
                if (item.payCode.excludeFromPaidHours) return false;
                return item.hourType !== "overtime";
            })
            ?.reduce((total, item) => {
                return total + getTimeSheetDuration(item);
            }, 0);

        const overtimeHours = userData
            ?.filter((item) => item.hourType === "overtime")
            ?.reduce((total, item) => {
                return total + getTimeSheetDuration(item);
            }, 0);
        return { totalAmount, workedHours, overtimeHours };
    }, [userData]);

    return (
        <div>
            <div
                className="py-3 px-4 d-flex gap-2 align-items-center border rounded"
                style={{ background: "#f2eaf8" }}
            >
                <h3 className="text-dark p-0 m-0">
                    {user.employeeNumber}
                </h3>
                <span className="text-dark p-0 m-0">
                    {`(${user.lastName}, ${user.firstName})`}
                </span>
                <span className="text-dark font-weight-bold p-0 m-0 mx-4">
                    <FontAwesomeIcon icon={faBuildingColumns} />  {user.primaryCompany.name}
                </span>

                <div className="d-flex align-items-center gap-2 mx-3 text-dark">
                    <h4 className="m-0 p-0">{t("worked-hours")}:</h4>
                    <span>{renderDurationAsFormat(workedHours, "HH:mm")}</span>
                </div>

                <div className="d-flex align-items-center gap-2 mx-3 text-dark">
                    <h4 className="m-0 p-0">{t("overtime-hours")}:</h4>
                    <span>{renderDurationAsFormat(overtimeHours, "HH:mm")}</span>
                </div>

                <div className="d-flex align-items-center gap-2 mx-3 text-dark">
                    <h4 className="m-0 p-0">{t("total-amount")}:</h4>
                    <span>{currencyFormatter(totalAmount, 2, currency)}</span>
                </div>
            </div>
            <Item table={table} />
        </div>
    );
}

export default Content;
